import React from 'react';
import classNames from 'classnames';
import CustomButton from '../../components-library/buttons/CustomButton';
import PlusIcon from '../../assets/logo/PlusIcon';
import { CompiledProduct } from '../../providers/MenuProvider';
import useConfig from '../../hooks/ui/useConfig';
import { ProductInterface } from '../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import RestrictedTooltip from '../../components-library/menu/restricted/RestrictedTooltip';
import {
  RESTRICTED_BG_CLASS,
  RESTRICTED_FG_HEX,
  RESTRICTED_IMAGE_STYLE,
} from '../../components-library/menu/restricted/constants';

type Props = {
  product: CompiledProduct;
  onClick?: () => void;
  hasAddBtn?: boolean;
  isRestricted?: boolean;
  restrictedTooltip?: string;
};

const OfferProduct = ({ product, onClick, hasAddBtn, isRestricted, restrictedTooltip }: Props) => {
  const { logo_url } = useConfig();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OfferModal' });
  const restrictedImageStyle = isRestricted ? RESTRICTED_IMAGE_STYLE : undefined;

  return (
    <article
      className={classnames('h-full overflow-hidden relative flex flex-col rounded-lg border', {
        'cursor-pointer': !!onClick && !isRestricted,
        'cursor-default': isRestricted,
      })}
      onClick={isRestricted ? undefined : onClick}
    >
      <BestSeller product={product} />
      <img
        src={product.thumbnail_url ?? logo_url}
        alt={product.name}
        className={classNames('object-cover w-full aspect-square', {
          'size-full !object-contain bg-brand-card': !product.thumbnail_url,
        })}
        style={restrictedImageStyle}
      />
      <div className="p-md justify-between flex flex-col h-full space-y-md">
        <p
          className={classnames('font-normal text-[15px] text-brand-text-primary line-clamp-1', {
            'opacity-50': isRestricted,
          })}
        >
          {product.name}
        </p>
        {hasAddBtn && isRestricted && <RestrictedAddButton label={t('buttons.add')} tooltip={restrictedTooltip} />}
        {hasAddBtn && !isRestricted && !!onClick && (
          <CustomButton
            customIcon={<PlusIcon className="mr-1 size-sm" color="white" />}
            className="h-9 !m-0 text-white text-sm"
          >
            {t('buttons.add')}
          </CustomButton>
        )}
      </div>
    </article>
  );
};

const RestrictedAddButton = ({ label, tooltip }: { label: string; tooltip?: string }) => {
  const button = (
    <button
      type="button"
      aria-disabled
      className={`h-9 rounded-lg flex items-center justify-center ${RESTRICTED_BG_CLASS} cursor-not-allowed text-sm w-full`}
      onClick={(e) => e.stopPropagation()}
    >
      <PlusIcon className="mr-1 size-sm" color={RESTRICTED_FG_HEX} />
      <span style={{ color: RESTRICTED_FG_HEX }}>{label}</span>
    </button>
  );

  return <RestrictedTooltip title={tooltip}>{button}</RestrictedTooltip>;
};

const BestSeller = ({ product }: { product: ProductInterface }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });

  if (!product.popularity_rank) {
    return null;
  }

  return (
    <div className="flex gap-x-1 font-medium text-xs items-center justify-start absolute top-2 left-2 bg-brand-card rounded-full px-xs py-[2px] shadow-icon dark:shadow-none">
      <p className="lg:text-xs text-2xs font-medium">
        🏅 {t('labels.most_popular', { rank: product?.popularity_rank })}
      </p>
    </div>
  );
};

export default OfferProduct;
